import Accordion from "@mui/material/Accordion";
import AccordionSummary from "@mui/material/AccordionSummary";
import AccordionDetails from "@mui/material/AccordionDetails";
import Typography from "@mui/material/Typography";
import {EquipmentSelectProps} from "../../../../../types/baseTypes";
import {convertMinutesToHours, formatCurrency, getEquipmentName} from "../../../../../util";
import {useTranslation} from "react-i18next";
import {palette} from "../../../../../tenants/kolula/layout/palette";
import {useSelector} from "react-redux";
import {RootState} from "../../../../../redux/store";

interface EquipmentListAccordionProps extends EquipmentSelectProps {
    expanded: boolean; // Whether this accordion is expanded
    onAccordionChange: () => void; // Function to handle accordion expansion
}

export default function EquipmentListAccordion(props: EquipmentListAccordionProps) {
    const {t, i18n} = useTranslation();
    const tenantConfig = useSelector((state: RootState) => state.tenantConfig.config);

    const priceScales = props.equipment.priceCategoryInfo?.priceScale?.reduce((acc: Record<string, number[]>, curr) => {
        if (!acc[curr.durationInMinutes]) {
            acc[curr.durationInMinutes] = [curr?.price ?? 0];
        } else {
            acc[curr.durationInMinutes].push(curr?.price ?? 0);
        }
        return acc;
    }, {});

    const sortedDurations = Object.keys(priceScales || {})
        .map(Number)
        .sort((a, b) => a - b);

    const sortedPriceScales = sortedDurations.reduce((acc: Record<string, number[]>, duration) => {
        acc[duration] = priceScales[duration].sort((a, b) => a - b);
        return acc;
    }, {});

    const lowestMinute = sortedDurations?.length > 0 ? sortedDurations[0] : null;
    const lowestMinutePrices = lowestMinute !== null ? sortedPriceScales[lowestMinute] : [0];

    const formattedPrice = new Intl.NumberFormat(i18n.language, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    }).format(lowestMinutePrices[0]);

    const priceIncludeInterval = Object.values(priceScales).some((priceScale) => priceScale.length > 1);

    return (
        <div className="w-full">
            <Accordion
                expanded={props.expanded} // Control the expansion state
                onChange={props.onAccordionChange} // Handle expansion state changes
                sx={{
                    bgcolor: props.expanded ? palette.accordion.expanded : palette.accordion.collapse,
                    boxShadow: "none",
                    borderRadius: 0,
                    padding: "12px",
                }}
            >
                <AccordionSummary aria-controls="panel1-content" id="panel1-header">
                    <div className={`flex justify-start w-full cursor-pointer`}>
                        <div className="flex-shrink-0 mr-8">
                            <img className="rounded" src={props.image} alt="" width={88} height={66}/>
                        </div>
                        <div className="" style={{maxWidth: "calc(100% - 96px)"}}>
                            <Typography
                                variant="body1"
                                sx={{
                                    display: "-webkit-box",
                                    WebkitBoxOrient: "vertical",
                                    WebkitLineClamp: props.expanded ? 10 : 2, // Dynamically set the line clamp
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                    fontWeight: "600",
                                    letterSpacing: "0.32px",
                                    lineHeight: "20px",
                                    maxHeight: props.expanded ? "auto" : `${2 * 24}px`, // Adjust max-height based on expansion
                                }}
                            >
                                {getEquipmentName(props.name, props?.langNames, "title")}
                            </Typography>
                            <Typography variant="body1">
                                {t("book_location.from")}{" "}
                                <span className="font-bold leading-[20px] tracking-[0.02em]">
                                  <span>{formattedPrice}</span> <span>{tenantConfig.currencySymbol}</span>
                                  <span> / {lowestMinute ? convertMinutesToHours(lowestMinute) : 0}</span>
                                </span>
                            </Typography>
                        </div>
                    </div>
                </AccordionSummary>
                <AccordionDetails sx={{paddingTop: 0, marginTop: "20px"}}>
                    <div>
                        <p className="font-bold leading-[20px] tracking-[0.02em]">
                            {t("book_location.description")}
                        </p>
                        <p className="text-itemListBorder">
                            {getEquipmentName(props.equipment.description, props?.langNames, "description")}
                        </p>
                    </div>
                    <div className={`h-[1px] w-full my-5 bg-primary`}></div>
                    <div className="flex flex-col items-center justify-between w-full">
                        <div className="flex items-center justify-between w-full">
                            <div className="font-bold leading-[20px] tracking-[0.02em]">
                                {t("book_location.prices")}
                            </div>
                            <div>{t("book_location.includes_tax")}</div>
                        </div>
                        {priceIncludeInterval && <div className="flex items-center justify-between w-full">
                            <div className="font-semibold leading-[20px] tracking-[0.02em]">
                                {t("general.duration")}
                            </div>
                            <div className={"font-semibold"}>{t("book_location.from")}</div>
                        </div>}
                        {Object.entries(priceScales)?.map(([durationInMinutes, prices]) => {
                            return (
                                <div className="flex items-center justify-between w-full text-itemListBorder" key={`${props.equipment.id}-${durationInMinutes}`}>
                                    <div>{convertMinutesToHours(Number(durationInMinutes), true)}</div>
                                    <div>
                                        {prices?.length > 0 ? formatCurrency(prices[0], i18n.language, tenantConfig.currency) : 0}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </AccordionDetails>
            </Accordion>
        </div>
    );
}
